import { CallExpressionShape, type ObjectShape, type Shape } from '@servicenow/sdk-build-core'
import { isRunAfterCallee } from './start-rule-resolver'

export interface RedundantEdge {
    /** The Now.ID key of the node that has the redundant dependency (e.g. 'lane_c') */
    nodeId: string
    /** The Now.ID key of the dependency that is redundant (e.g. 'lane_a') */
    redundantDepId: string
    /** The Now.ID key of the other dependency through which redundantDepId is already reachable (e.g. 'lane_b') */
    throughId: string
    /** The call expression shape of the node, used as the diagnostic source */
    shape: Shape
    /** Display label derived from the node's callee name (e.g. 'Lane', 'Activity') */
    nodeTypeLabel: string
}

const NOW_ID_PATTERN = /^Now\.ID\[['"]([^'"]+)['"]\]$/

/**
 * Extract the Now.ID key string from a shape by inspecting its code representation.
 * Returns the key (e.g. 'lane_a') if the shape generates Now.ID['lane_a'], or undefined.
 * Works for NowIdShape and any ElementAccessExpressionShape whose getCode() returns
 * the Now.ID[...] pattern — no instanceof or module-identity assumptions needed.
 */
function getNowIdKey(shape: Shape): string | undefined {
    return shape.getCode().match(NOW_ID_PATTERN)?.[1]
}

/**
 * Extract the Now.ID key string from a node config's $id field.
 * Returns undefined if the $id is not a Now.ID element access expression.
 */
function getNodeIdKey(config: ObjectShape): string | undefined {
    return getNowIdKey(config.get('$id'))
}

/**
 * Resolve a node's call expression to the config object containing its $id.
 *
 * Supports both shapes used by playbook APIs:
 *  - Lane({ config: { $id, ... }, activities: ... })       → config at arg 0, nested under 'config'
 *  - Activity(def, { $id, ... })                            → config at arg 1
 *  - Flat objects at arg 0 (test fixtures, custom callees)  → config at arg 0
 */
function getConfigWithId(callExpr: CallExpressionShape): ObjectShape | undefined {
    for (const argIndex of [0, 1]) {
        const arg = callExpr.getArgument(argIndex)?.ifObject()
        if (!arg) {
            continue
        }
        const nested = arg.get('config')?.isUndefined() === false ? arg.get('config').ifObject() : arg
        if (nested && getNowIdKey(nested.get('$id')) !== undefined) {
            return nested
        }
    }
    return undefined
}

/**
 * Extract the Now.ID keys of all nodes referenced in a Run.After() call.
 * Resolves both direct call expressions and identifier references (e.g. lane_0, activity_0).
 * Handles both flat ({ $id, ... }) and nested ({ config: { $id, ... } }) config shapes,
 * as well as Activity's two-arg signature where config is at arg 1.
 */
function getRunAfterDepKeys(config: ObjectShape, variables: Map<string, CallExpressionShape>): string[] {
    const startRuleShape = config.get('startRule')
    if (!startRuleShape?.is(CallExpressionShape)) {
        return []
    }

    const runAfterCall = startRuleShape.as(CallExpressionShape)
    if (!isRunAfterCallee(runAfterCall.getCallee())) {
        return []
    }

    const keys: string[] = []
    for (const arg of runAfterCall.getArguments()) {
        let callExpr: CallExpressionShape | undefined

        if (arg.is(CallExpressionShape)) {
            callExpr = arg.as(CallExpressionShape)
        } else if (arg.isIdentifier()) {
            callExpr = variables.get(arg.asIdentifier().getName())
        }

        if (!callExpr) {
            continue
        }

        const cfg = getConfigWithId(callExpr)
        const key = cfg ? getNowIdKey(cfg.get('$id')) : undefined
        if (key) {
            keys.push(key)
        }
    }

    return keys
}

/**
 * Build the transitive closure (all reachable nodes) from a given node using DFS.
 * Uses a cache to avoid recomputing for the same node.
 */
function buildReachabilitySet(
    nodeId: string,
    graph: Map<string, string[]>,
    cache: Map<string, Set<string>>
): Set<string> {
    if (cache.has(nodeId)) {
        return cache.get(nodeId)!
    }

    // Mark as in-progress with empty set to handle cycles gracefully
    const reachable = new Set<string>()
    cache.set(nodeId, reachable)

    for (const dep of graph.get(nodeId) ?? []) {
        reachable.add(dep)
        for (const transitive of buildReachabilitySet(dep, graph, cache)) {
            reachable.add(transitive)
        }
    }

    return reachable
}

/**
 * Detect redundant edges in a collection of nodes that use Run.After() for ordering.
 *
 * An edge from node N to dependency D is redundant when D is already reachable
 * from N through another one of N's direct dependencies.
 *
 * Example: if nodes are A → B → C, and C also directly lists A in Run.After(),
 * then C → A is redundant because C → B → A already covers it.
 *
 * This mirrors the "Redundant start rule" warning in Playbook Designer and applies
 * to any ordered collection (lanes, activities, etc.).
 *
 * @param children - The parsed call expressions and their configs from processChildCollectionToRecord
 * @param variables - Map of identifier names to their resolved call expressions (e.g. 'lane_0' → Lane(...))
 * @returns Array of redundant edges, each containing the shape for diagnostic reporting
 */
export function detectRedundantEdges(
    children: Array<{ callExpr: CallExpressionShape; childConfig: ObjectShape }>,
    variables: Map<string, CallExpressionShape>
): RedundantEdge[] {
    // Build graph: nodeId → direct dependency ids
    const graph = new Map<string, string[]>()
    const nodeCallExprs = new Map<string, CallExpressionShape>()

    for (const { callExpr, childConfig } of children) {
        const id = getNodeIdKey(childConfig)
        if (!id) {
            continue
        }
        const deps = getRunAfterDepKeys(childConfig, variables)
        graph.set(id, deps)
        nodeCallExprs.set(id, callExpr)
    }

    // Build reachability cache for all nodes
    const reachabilityCache = new Map<string, Set<string>>()

    const redundantEdges: RedundantEdge[] = []

    for (const [nodeId, deps] of graph.entries()) {
        // Need at least 2 dependencies to have a redundant one
        if (deps.length < 2) {
            continue
        }

        for (const dep of deps) {
            // Check if dep is reachable from any other direct dependency of this node
            const otherDeps = deps.filter((d) => d !== dep)
            for (const otherDep of otherDeps) {
                const reachable = buildReachabilitySet(otherDep, graph, reachabilityCache)
                if (reachable.has(dep)) {
                    const callExpr = nodeCallExprs.get(nodeId)!
                    const callee = callExpr.getCallee()
                    const nodeTypeLabel =
                        callee
                            .split('.')
                            .at(-1)
                            ?.replace(/^\w/, (c) => c.toUpperCase()) ?? callee
                    redundantEdges.push({
                        nodeId,
                        redundantDepId: dep,
                        throughId: otherDep,
                        shape: callExpr,
                        nodeTypeLabel,
                    })
                    break // Only report once per redundant dep
                }
            }
        }
    }

    return redundantEdges
}
